/**
 * Cookie consent utilities — single source of truth for what cookies the user
 * has accepted. Read/written from localStorage so it persists across sessions.
 *
 * Consent shape:
 *   {
 *     necessary: true,           // always true (functional cookies)
 *     analytics: boolean,        // page view tracking, admin analytics
 *     marketing: boolean,        // future: ads / retargeting
 *     timestamp: ISO-8601 string
 *   }
 */
const STORAGE_KEY = "kk_cookie_consent_v1";
const CHANGE_EVENT = "kk:cookie-consent-change";

export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || !parsed) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function hasResponded() {
  return getConsent() !== null;
}

export function setConsent(consent) {
  const payload = {
    necessary: true,
    analytics: !!consent.analytics,
    marketing: !!consent.marketing,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  // Notify any listeners in the same tab (storage event only fires in OTHER tabs).
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: payload }));
  return payload;
}

export function acceptAll() {
  return setConsent({ analytics: true, marketing: true });
}

export function rejectAll() {
  return setConsent({ analytics: false, marketing: false });
}

export function isAllowed(category) {
  const c = getConsent();
  if (!c) return false;
  if (category === "necessary") return true;
  return !!c[category];
}

export function onConsentChange(listener) {
  window.addEventListener(CHANGE_EVENT, listener);
  return () => window.removeEventListener(CHANGE_EVENT, listener);
}
